import React, { useState } from "react";

import { useHooks } from "../../../../services/useHooks";

//: The vocabularies the server accepts. `feedback_wave2._HOOK_TYPES` and
//: `_PLATFORMS` are the authority — an edit offering a value the server refuses
//: would be a form that cannot be saved, so a backend test pins these two lists
//: against those sets.
export const HOOK_TYPES = ["CURIOSITY_GAP", "CONTRARIAN", "HIGH_EMOTION",
                           "BOLD_CLAIM", "DIRECT_ADDRESS", "STORY_OPEN"];
export const HOOK_PLATFORMS = ["META", "TIKTOK", "BOTH"];

/**
 * Workstation · Ads & Creative — **step 3, Hook Selection, in v2.**
 *
 * WAVE 3, SCREEN 3. The look is v2's; the behaviour is `services/useHooks`,
 * which v1's `HookSelectionStep` also calls.
 *
 * WHAT THE SWEEP FOUND AND FIXED, all of it in the hook so v1 got it too:
 *
 *   · A failed read, a new session and an offer with no hooks were one blank
 *     list with nothing written on it.
 *   · A new session never fetched at all.
 *   · Nothing was scoped to a session.
 *   · Regeneration appended in silence.
 *   · Three failure paths used `alert()`.
 *   · **Continue threw the response away.** `select_hooks` reports how many it
 *     actually recorded, and the screen never read it — so selecting twelve
 *     hooks and having nine recorded looked exactly like selecting twelve, and
 *     script generation then ran on nine. That one is this screen's own.
 *
 * MA-2 landed before this port on purpose: the 50-row ceiling that was hiding
 * 80 hooks on one real offer is gone, and the read now reports how many the
 * offer holds — so this screen can say "showing 12 of 130" instead of implying
 * it shows everything.
 */
export default function HookStep({ offer, sessionId, isNewSession,
                                   sessionStartTime, angleId, refreshKey,
                                   onComplete }) {
  const H = useHooks({ offer, sessionId, isNewSession, sessionStartTime,
                       angleId, onComplete, refreshKey });

  // FOUNDER REQUEST 2026-09-10: "There should be an edit button and refine
  // button for each hook." Same two gestures the Angle step has — refine asks
  // the model to rewrite this one hook from a note and keeps it anchored to the
  // angle; edit applies the operator's own words with no model in the loop.
  const [refineFor, setRefineFor] = useState(null);   // element_id
  const [refineText, setRefineText] = useState("");
  const [editFor, setEditFor] = useState(null);       // element_id
  const [draft, setDraft] = useState({});

  const openEdit = (hook) => {
    setEditFor(hook.element_id);
    setRefineFor(null);
    // Seeded from the hook, so an untouched field round-trips unchanged rather
    // than being blanked by an empty form.
    setDraft({
      content: hook.content || "",
      hook_type: hook.hook_type || "CURIOSITY_GAP",
      platform_optimisation: hook.platform_optimisation || "BOTH",
      hook_rationale: hook.hook_rationale || "",
    });
  };

  const showingNote =
    H.totalForOffer != null && H.totalForOffer > H.hooks.length
      ? `Showing ${H.hooks.length} of ${H.totalForOffer} hooks on this offer.`
      : null;

  return (
    <div className="ws-step" data-testid="ws-hook-step">
      <div className="ws-step-head">
        <div>
          <h3 className="ws-step-title">Hooks</h3>
          <p className="dim">
            The opening lines the scripts will be built from. Choose as many as
            you want to write scripts for.
          </p>
        </div>
        <button
          type="button"
          className={H.hooks.length === 0 ? "btn-primary btn-sm" : "btn-ghost btn-sm"}
          onClick={H.generateHooks}
          disabled={H.generating || H.loadState === "loading"}
          data-testid={H.hooks.length === 0 ? "ws-hook-generate" : "ws-hook-regenerate"}
        >
          {H.generating
            ? (H.hooks.length === 0 ? "Generating…" : "Generating…")
            : (H.hooks.length === 0 ? "Generate Hooks" : "Generate more")}
        </button>
      </div>

      {H.loadState === "loading" && H.hooks.length === 0 ? (
        <div className="state ws-state" data-testid="ws-hook-loading">
          Reading this offer's hooks…
        </div>
      ) : null}

      {H.loadState === "failed" ? (
        <div className="state ws-state state-error" role="alert" data-testid="ws-hook-load-failed">
          <p>The hooks could not be read: {H.loadError}</p>
          <p className="dim">This is a failure, not an empty offer. Nothing has been lost.</p>
          <button type="button" className="btn-ghost btn-sm"
                  onClick={H.retryLoad} data-testid="ws-hook-retry">
            Try again
          </button>
        </div>
      ) : null}

      {H.loadState === "ok" && H.hooks.length === 0 ? (
        <div className="state ws-state" data-testid="ws-hook-empty">
          {H.allCount > 0
            ? `This session has no hooks yet. The offer holds ${H.allCount} from earlier work, not shown here.`
            : "No hooks yet for this offer. Generate a set to begin."}
        </div>
      ) : null}

      {H.note ? (
        <p className="ws-step-note" role="status" data-testid="ws-hook-note">
          {H.note.text}
          {H.note.count > 1 ? <span className="dim"> (asked {H.note.count} times)</span> : null}
        </p>
      ) : null}

      {H.actionError ? (
        <p className="ws-step-note state-error" role="alert" data-testid="ws-hook-error">
          {H.actionError}
        </p>
      ) : null}

      {H.hooks.length > 0 ? (
        <ul className="ws-hook-list">
          {H.hooks.map((hook) => {
            const picked = H.selectedHooks.includes(hook.element_id);
            return (
              <li
                key={hook.element_id}
                className={`ws-hook ${picked ? "is-picked" : ""}`}
                onClick={() => H.toggleHook(hook.element_id)}
                data-testid={`ws-hook-${hook.element_id}`}
              >
                <span className={`ws-hook-tick ${picked ? "is-on" : ""}`} aria-hidden="true">
                  {picked ? "✓" : ""}
                </span>
                <div className="ws-hook-body">
                  <div className="ws-hook-meta dim">
                    {/* A hook with no number has not been promoted to the
                        library yet. Saying "—" and explaining beats a blank. */}
                    <span className="ws-hook-number"
                          title={hook.element_number ? undefined
                                                     : "Not yet promoted to the library"}>
                      {hook.element_number || "—"}
                    </span>
                    {hook.hook_type ? (
                      <span className={`ws-chip ht-${(hook.hook_type || "").toLowerCase()}`}>
                        {hook.hook_type.replace(/_/g, " ")}
                      </span>
                    ) : null}
                    {hook.platform_optimisation ? (
                      <span className="ws-chip">{hook.platform_optimisation}</span>
                    ) : null}
                    {!hook.session_id ? (
                      <span className="ws-chip is-quiet"
                            data-testid={`ws-hook-unstamped-${hook.element_id}`}
                            title="Written before hooks recorded their session">
                        session unknown
                      </span>
                    ) : null}
                  </div>
                  <p className="ws-hook-content">“{hook.content}”</p>
                  {hook.hook_rationale ? (
                    <p className="dim ws-hook-rationale">{hook.hook_rationale}</p>
                  ) : null}

                  {/* The row itself toggles selection, so every control here
                      stops the click. Same reason the Angle step does. */}
                  <div className="ws-hook-actions" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="btn-ghost btn-sm"
                            disabled={H.busyHook === hook.element_id}
                            onClick={() => {
                              setRefineFor(refineFor === hook.element_id ? null : hook.element_id);
                              setRefineText("");
                              setEditFor(null);
                            }}
                            data-testid={`ws-hook-refine-btn-${hook.element_id}`}>
                      {H.busyHook === hook.element_id ? "Working…" : "Refine"}
                    </button>
                    <button type="button" className="btn-ghost btn-sm"
                            disabled={H.busyHook === hook.element_id}
                            onClick={() => (editFor === hook.element_id
                              ? setEditFor(null) : openEdit(hook))}
                            data-testid={`ws-hook-edit-btn-${hook.element_id}`}>
                      Edit text
                    </button>
                    {hook.refined_at ? (
                      <span className="dim" data-testid={`ws-hook-refined-${hook.element_id}`}>
                        refined
                      </span>
                    ) : null}
                    {hook.last_edit_kind === "OPERATOR_EDIT" ? (
                      <span className="dim" data-testid={`ws-hook-edited-${hook.element_id}`}>
                        edited by hand
                      </span>
                    ) : null}
                  </div>

                  {refineFor === hook.element_id ? (
                    <div className="ws-hook-drawer" onClick={(e) => e.stopPropagation()}
                         data-testid={`ws-hook-refine-drawer-${hook.element_id}`}>
                      <p className="dim">
                        Say what to change. The model rewrites this hook only —
                        the others are untouched, and the angle is kept.
                      </p>
                      <textarea rows={2} value={refineText}
                                onChange={(e) => setRefineText(e.target.value)}
                                placeholder="Make it shorter and lead with the deadline."
                                data-testid={`ws-hook-refine-text-${hook.element_id}`} />
                      <div className="ws-hook-drawer-foot">
                        <button type="button" className="btn-ghost btn-sm"
                                onClick={() => { setRefineFor(null); setRefineText(""); }}
                                data-testid={`ws-hook-refine-cancel-${hook.element_id}`}>
                          Discard
                        </button>
                        <button type="button" className="btn-primary btn-sm"
                                disabled={refineText.trim().length < 3
                                          || H.busyHook === hook.element_id}
                                onClick={async () => {
                                  const ok = await H.refineHook(hook.element_id, refineText);
                                  if (ok) { setRefineFor(null); setRefineText(""); }
                                }}
                                data-testid={`ws-hook-refine-save-${hook.element_id}`}>
                          {H.busyHook === hook.element_id ? "Refining…" : "Refine this hook"}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {editFor === hook.element_id ? (
                    <div className="ws-hook-drawer" onClick={(e) => e.stopPropagation()}
                         data-testid={`ws-hook-edit-drawer-${hook.element_id}`}>
                      <p className="dim">
                        Your words, written as they are — no model. Refine usually
                        gives a better-integrated result.
                      </p>
                      <label>
                        <span className="dim">Hook</span>
                        <textarea rows={2} value={draft.content ?? ""}
                                  onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))}
                                  data-testid={`ws-hook-edit-content-${hook.element_id}`} />
                      </label>
                      <div className="ws-hook-drawer-row">
                        <label>
                          <span className="dim">Type</span>
                          <select value={draft.hook_type ?? "CURIOSITY_GAP"}
                                  onChange={(e) => setDraft((d) => ({ ...d, hook_type: e.target.value }))}
                                  data-testid={`ws-hook-edit-type-${hook.element_id}`}>
                            {HOOK_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                          </select>
                        </label>
                        <label>
                          <span className="dim">Platform</span>
                          <select value={draft.platform_optimisation ?? "BOTH"}
                                  onChange={(e) => setDraft((d) => ({ ...d, platform_optimisation: e.target.value }))}
                                  data-testid={`ws-hook-edit-platform-${hook.element_id}`}>
                            {HOOK_PLATFORMS.map((pl) => <option key={pl} value={pl}>{pl}</option>)}
                          </select>
                        </label>
                      </div>
                      <label>
                        <span className="dim">Why it works</span>
                        <textarea rows={2} value={draft.hook_rationale ?? ""}
                                  onChange={(e) => setDraft((d) => ({ ...d, hook_rationale: e.target.value }))}
                                  data-testid={`ws-hook-edit-rationale-${hook.element_id}`} />
                      </label>
                      <div className="ws-hook-drawer-foot">
                        <button type="button" className="btn-ghost btn-sm"
                                onClick={() => { setEditFor(null); setDraft({}); }}
                                data-testid={`ws-hook-edit-cancel-${hook.element_id}`}>
                          Discard
                        </button>
                        <button type="button" className="btn-primary btn-sm"
                                disabled={!(draft.content || "").trim()
                                          || H.busyHook === hook.element_id}
                                onClick={async () => {
                                  const ok = await H.editHook(hook.element_id, draft);
                                  if (ok) { setEditFor(null); setDraft({}); }
                                }}
                                data-testid={`ws-hook-edit-save-${hook.element_id}`}>
                          {H.busyHook === hook.element_id ? "Saving…" : "Save my words"}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {H.hiddenOtherSession || H.hiddenUnstamped ? (
        <p className="dim ws-step-note" data-testid="ws-hook-hidden-note">
          Not shown:{" "}
          {[
            H.hiddenOtherSession ? `${H.hiddenOtherSession} from other sessions on this offer` : null,
            H.hiddenUnstamped ? `${H.hiddenUnstamped} that predate session tracking (Cipher cannot tell which session made them)` : null,
          ].filter(Boolean).join("; ")}
          . This view is scoped to the current session.
        </p>
      ) : null}

      {H.predateNote > 0 ? (
        <p className="dim ws-step-note" data-testid="ws-hook-predate-note">
          Showing {H.predateNote} hook{H.predateNote > 1 ? "s" : ""} from this
          offer that predate session tracking — Cipher cannot tell which session
          made them.
        </p>
      ) : null}

      {/* MA-2 gives the offer's true total, so a filtered view can say so
          rather than implying it is everything — which is the same lie the
          50-row ceiling told. */}
      {showingNote ? (
        <p className="dim ws-step-note" data-testid="ws-hook-showing-note">{showingNote}</p>
      ) : null}

      {H.hooks.length > 0 ? (
        <div className="ws-step-foot">
          <div className="ws-hook-regen">
            <textarea
              rows={2}
              value={H.feedback}
              onChange={(e) => H.setFeedback(e.target.value)}
              placeholder="What should the next batch do differently? It will be told."
              data-testid="ws-hook-feedback"
            />
            <button type="button" className="btn-ghost btn-sm"
                    onClick={H.generateHooks} disabled={H.generating}
                    data-testid="ws-hook-generate-more">
              {H.generating ? "Generating…" : H.feedback ? "Generate with feedback" : "Generate more"}
            </button>
          </div>

          <div className="ws-hook-confirm">
            <span className="dim">
              {H.selectedHooks.length === 0
                ? "Select one or more hooks to continue"
                : `${H.selectedHooks.length} hook${H.selectedHooks.length > 1 ? "s" : ""} selected`}
            </span>
            <button type="button" className="btn-primary btn-sm"
                    onClick={H.confirmHooks}
                    disabled={H.saving || H.selectedHooks.length === 0}
                    data-testid="ws-hook-confirm">
              {H.saving ? "Saving…" : "Continue"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
